import dayjs from 'dayjs';
import { ChangeEvent, FC, ReactNode, useEffect, useState } from 'react';

import { ActionIcon, Box, Flex } from '@mantine/core';

import useAppStore from '@/shared/store/app.store';
import { TDateRange } from '@/shared/types/app.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { WheelPicker } from '@/shared/uikit/components/picker/WheelPicker/WheelPicker';

import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import classes from './Picker.module.css';

const hourItems = Array.from({ length: 24 }, (_, index) => ({
	value: index.toString().padStart(2, '0'),
	label: index.toString().padStart(2, '0'),
}));

const minuteItems = Array.from({ length: 4 }, (_, index) => ({
	value: `${(index * 15).toString().padStart(2, '0')}`,
	label: `${(index * 15).toString().padStart(2, '0')}`,
}));

type PickerProps = {
	initValue: TDateRange;
	opened: boolean;
	title?: string;
	description?: ReactNode;
	isTaxi?: boolean;
	close: () => void;
	onChange: (date: TDateRange) => void;
};

export const Picker: FC<PickerProps> = ({
	initValue,
	opened,
	title = 'Время',
	description,
	isTaxi = false,
	close,
	onChange,
}) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const [currentSelector, setCurrentSelector] = useState(0);
	const [start, setStart] = useState<Date | null>(initValue[0]);
	const [end, setEnd] = useState<Date | null>(initValue[1]);

	const handleChangeHour = (hour: number) => {
		hour = hour < 0 ? 23 : hour > 23 ? 0 : hour;

		if (currentSelector === 0) {
			return setStart(dayjs(start).set('hour', hour).set('second', 0).toDate());
		}

		setEnd(dayjs(end).clone().set('hour', hour).set('second', 0).toDate());
	};

	const handleChangeMinute = (minute: number) => {
		if (currentSelector === 0) {
			if (dayjs(end).isBefore(dayjs(start)) || dayjs(end).isSame(dayjs(start))) {
				setEnd(dayjs(start).add(15, 'minute').toDate());
			}
			return setStart(dayjs(start).set('minute', minute).set('second', 0).toDate());
		}

		if (dayjs(end).isBefore(dayjs(start)) || dayjs(end).isSame(dayjs(start))) {
			setStart(dayjs(end).add(-15, 'minute').toDate());
		}
		setEnd(dayjs(end).set('minute', minute).set('second', 0).toDate());
	};

	const handleChangeTime = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const rawTime = event.target.value.split(':');

		if (currentSelector === 0) {
			setStart(
				dayjs(start)
					.set('hour', Number(rawTime[0]))
					.set('minute', Number(rawTime[1]))
					.set('second', 0)
					.toDate(),
			);
		} else {
			setEnd(
				dayjs(end)
					.set('hour', Number(rawTime[0]))
					.set('minute', Number(rawTime[1]))
					.set('second', 0)
					.toDate(),
			);
		}
	};

	useEffect(() => {
		const startInit = dayjs().hour(dayjs(start).hour()).minute(dayjs(start).minute()).second(0);
		const endInit = dayjs().hour(dayjs(end).hour()).minute(dayjs(end).minute()).second(0);

		if (currentSelector === 0) {
			if (isTaxi) {
				const endStartDiff = dayjs(endInit).diff(dayjs(startInit), 'minute');

				if (endStartDiff < 30 || endStartDiff > 30) {
					setEnd(dayjs(start).add(30, 'minute').toDate());
				}
			} else {
				if (dayjs(endInit).isBefore(dayjs(startInit))) {
					setEnd(dayjs(start).add(15, 'minute').toDate());
				}
			}
		} else {
			if (dayjs(endInit).isBefore(dayjs(startInit))) {
				setStart(dayjs(end).add(-15, 'minute').toDate());
			}
		}
	}, [start, end]);

	const content = (
		<>
			<Flex align="center" justify="center">
				{description}
			</Flex>

			<Flex align="center" gap={12}>
				<LabelInput
					label="Начало"
					value={dayjs(start ?? initValue[0]).format('HH:mm') ?? ''}
					placeholder="Не выбрано"
					onClick={() => setCurrentSelector(0)}
					// onChange={(event) => handleChangeTime(event)}
					readOnly
				/>
				<Box>-</Box>
				<LabelInput
					label="Конец"
					value={dayjs(end ?? initValue[1]).format('HH:mm') ?? ''}
					placeholder="Не выбрано"
					// onClick={() => setCurrentSelector(1)}
					// onChange={(event) => handleChangeTime(event)}
					{...(isTaxi
						? { disabled: true }
						: { readOnly: true, onClick: () => setCurrentSelector(1) })}
				/>
			</Flex>
			<Flex align="center" justify="center" gap={44} py={16}>
				<ActionIcon
					color="grey"
					variant="light"
					classNames={classes}
					onClick={() => handleChangeHour(dayjs(currentSelector === 0 ? start : end).hour() - 1)}
				>
					<IconChevronUp />
				</ActionIcon>
				<ActionIcon
					color="grey"
					variant="light"
					classNames={classes}
					onClick={() =>
						handleChangeMinute(dayjs(currentSelector === 0 ? start : end).minute() - 15)
					}
				>
					<IconChevronUp />
				</ActionIcon>
			</Flex>
			<Flex align="center" justify="center">
				{currentSelector === 0 && (
					<WheelPicker
						hourItems={hourItems}
						hourValue={
							hourItems[hourItems.map((el) => el.value).indexOf(String(dayjs(start).format('HH')))]
								?.value
						}
						onHourChange={handleChangeHour}
						minuteItems={minuteItems}
						minuteValue={
							minuteItems[
								minuteItems.map((el) => el.value).indexOf(String(dayjs(start).format('mm')))
							]?.value
						}
						onMinuteChange={handleChangeMinute}
						itemHeight={62}
						containerHeight={300}
					/>
				)}
				{currentSelector === 1 && (
					<WheelPicker
						hourItems={hourItems}
						hourValue={
							hourItems[hourItems.map((el) => el.value).indexOf(String(dayjs(end).format('HH')))]
								?.value
						}
						onHourChange={handleChangeHour}
						minuteItems={minuteItems}
						minuteValue={
							minuteItems[
								minuteItems.map((el) => el.value).indexOf(String(dayjs(end).format('mm')))
							]?.value
						}
						onMinuteChange={handleChangeMinute}
						itemHeight={62}
						containerHeight={300}
					/>
				)}
			</Flex>
			<Flex align="center" justify="center" gap={44} py={16}>
				<ActionIcon
					color="grey"
					variant="light"
					classNames={classes}
					onClick={() => handleChangeHour(dayjs(currentSelector === 0 ? start : end).hour() + 1)}
				>
					<IconChevronDown />
				</ActionIcon>

				<ActionIcon
					color="grey"
					variant="light"
					classNames={classes}
					onClick={() =>
						handleChangeMinute(dayjs(currentSelector === 0 ? start : end).minute() + 15)
					}
				>
					<IconChevronDown />
				</ActionIcon>
			</Flex>
		</>
	);

	if (isMobile)
		return (
			<BottomSheet
				opened={opened}
				onClose={close}
				title={title}
				withBackButton={false}
				withCloseButton={false}
				isSwipeable={false}
				buttons={[
					{
						children: 'Закрыть',
						variant: 'default',
						onClick: close,
					},
					{
						children: 'Выбрать',
						onClick: () => {
							onChange([start ?? initValue[0], end ?? initValue[1]]);
							close();
						},
					},
				]}
			>
				{content}
			</BottomSheet>
		);

	return (
		<ModalSheet
			opened={opened}
			onClose={close}
			title={title}
			withCloseButton={false}
			buttons={[
				{
					children: 'Закрыть',
					variant: 'default',
					onClick: close,
				},
				{
					children: 'Выбрать',
					onClick: () => {
						onChange([start ?? initValue[0], end ?? initValue[1]]);
						close();
					},
				},
			]}
			size="lg"
			centered
		>
			{content}
		</ModalSheet>
	);
};
